import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { RaisedQuestion } from '../../../types/live';

// A raised question in the presenter's queue, plus a client-side "new" flag used
// to drive the coalescing attention notice and the row highlight.
export interface QueuedRaise extends RaisedQuestion {
  // Client receipt time, used only as a stable fallback sort key if two raises
  // share a server timestamp; ordering is otherwise oldest-first by raised_at.
  received_at: number;
}

interface RaisedQuestionsControlProps {
  // FIFO queue (oldest first) of currently-unresolved raised questions. Owned by
  // the parent so it survives panel open/close and SSE reconnects.
  queue: QueuedRaise[];
  // Marks a raised question addressed (presenter handled it in the room).
  onAddress: (questionId: string) => Promise<void> | void;
}

const RELATIVE = (iso: string, now: number): string => {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((now - t) / 1000));
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
};

// Presenter-only raised-hands control: a persistent hand+count trigger beside
// the header, an on-demand FIFO queue panel (the source of truth), and a single
// coalescing attention notice when new raises arrive. Non-presenters never
// render this. See the UI/UX brief: the notice only attracts attention; all
// order/detail/resolution lives in the panel.
const RaisedQuestionsControl: React.FC<RaisedQuestionsControlProps> = ({ queue, onAddress }) => {
  const [open, setOpen] = useState(false);
  const [addressing, setAddressing] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Coalescing notice: shows the latest arrival (or a count when several land in
  // quick succession) for a few seconds, unless the panel is already open.
  const [notice, setNotice] = useState<{ text: string; count: number } | null>(null);
  const [announce, setAnnounce] = useState('');

  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seenIdsRef = useRef<Set<string>>(new Set());

  const count = queue.length;

  // Keep relative timestamps fresh while the panel is open.
  useEffect(() => {
    if (!open) return;
    setNow(Date.now());
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, [open]);

  // Detect newly-arrived raises (ids not seen before) and drive the notice +
  // a polite screen-reader announcement. Coalesces multiple arrivals into one
  // notice; suppresses the visual notice while the panel is open.
  useEffect(() => {
    const seen = seenIdsRef.current;
    const fresh = queue.filter((q) => !seen.has(q.question_id));
    queue.forEach((q) => seen.add(q.question_id));
    // Drop ids that have left the queue so a re-raise announces again.
    const live = new Set(queue.map((q) => q.question_id));
    seen.forEach((id) => { if (!live.has(id)) seen.delete(id); });

    if (fresh.length === 0) return;

    const latest = fresh[fresh.length - 1];
    setAnnounce(
      `New raised question from ${latest.display_name}${latest.role ? `, ${latest.role}` : ''}. ` +
        `${queue.length} unresolved ${queue.length === 1 ? 'question' : 'questions'} total.`
    );

    if (open) return; // Panel is the reveal point; skip the transient notice.

    setNotice((prev) => {
      const added = (prev?.count ?? 0) + fresh.length;
      return added > 1
        ? { text: `${added} new raised questions`, count: added }
        : { text: `${latest.display_name} raised a question`, count: 1 };
    });
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
    noticeTimer.current = setTimeout(() => setNotice(null), 8000);
  }, [queue, open]);

  useEffect(() => () => { if (noticeTimer.current) clearTimeout(noticeTimer.current); }, []);

  const openPanel = useCallback(() => {
    setOpen(true);
    setNotice(null);
    if (noticeTimer.current) clearTimeout(noticeTimer.current);
  }, []);

  const closePanel = useCallback(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, []);

  // Move focus into the panel on open; Escape closes and restores focus.
  useEffect(() => {
    if (!open) return;
    headingRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePanel();
    };
    const onClickOutside = (e: MouseEvent) => {
      if (
        panelRef.current &&
        !panelRef.current.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClickOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClickOutside);
    };
  }, [open, closePanel]);

  const handleAddress = async (questionId: string) => {
    setAddressing(questionId);
    try {
      await onAddress(questionId);
    } finally {
      setAddressing((prev) => (prev === questionId ? null : prev));
    }
  };

  const triggerLabel = useMemo(
    () => `Raised questions, ${count === 0 ? 'none waiting' : `${count} unresolved`}`,
    [count]
  );

  return (
    <div className="raised-control">
      {/* Polite live region: announces identity + count, not the question text. */}
      <div className="sr-only" role="status" aria-live="polite">{announce}</div>

      <button
        ref={triggerRef}
        type="button"
        className={`raised-trigger${count > 0 ? ' raised-trigger-active' : ''}${open ? ' raised-trigger-open' : ''}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={triggerLabel}
        onClick={() => (open ? closePanel() : openPanel())}
      >
        <span className="raised-trigger-icon" aria-hidden="true">✋</span>
        <span className="raised-trigger-text">Raised</span>
        <span className={`raised-trigger-badge${count === 0 ? ' raised-trigger-badge-empty' : ''}`} aria-hidden="true">
          {count > 99 ? '99+' : count}
        </span>
      </button>

      {notice && !open && (
        <div className="raised-notice" role="status">
          <span className="raised-notice-text">{notice.text}</span>
          <button type="button" className="raised-notice-view" onClick={openPanel}>View queue</button>
          <button
            type="button"
            className="raised-notice-close"
            aria-label="Dismiss notification"
            onClick={() => setNotice(null)}
          >
            ×
          </button>
        </div>
      )}

      {open && (
        <div
          ref={panelRef}
          className="raised-panel"
          role="dialog"
          aria-modal="false"
          aria-label={`Raised questions, ${count} unresolved`}
        >
          <div className="raised-panel-head">
            <h2 className="raised-panel-title" tabIndex={-1} ref={headingRef}>
              Raised questions{count > 0 ? ` (${count})` : ''}
            </h2>
            <button type="button" className="raised-panel-close" aria-label="Close raised questions" onClick={closePanel}>
              ×
            </button>
          </div>

          {count === 0 ? (
            <div className="raised-empty">
              <p className="raised-empty-title">No raised questions yet.</p>
              <p className="raised-empty-sub">Questions raised by participants will appear here.</p>
            </div>
          ) : (
            <ol className="raised-list">
              {queue.map((q, i) => {
                const grounded = q.question_kind === 'grounded';
                const busy = addressing === q.question_id;
                return (
                  <li key={q.question_id} className="raised-item">
                    <div className="raised-item-head">
                      <span className="raised-item-pos" aria-hidden="true">{i + 1}</span>
                      <div className="raised-item-who">
                        <span className="raised-item-name">{q.display_name}</span>
                        {q.role && <span className="raised-item-role">{q.role}</span>}
                      </div>
                      <span
                        className={`question-kind-badge question-kind-badge-${grounded ? 'grounded' : 'discussion'}`}
                      >
                        {grounded ? 'Grounded' : 'Discussion'}
                      </span>
                    </div>
                    <p className="raised-item-text">{q.text}</p>
                    <div className="raised-item-foot">
                      <span className="raised-item-time" title={new Date(q.raised_at).toLocaleTimeString()}>
                        {RELATIVE(q.raised_at, now)}
                      </span>
                      <button
                        type="button"
                        className="meeting-btn meeting-btn-primary meeting-btn-sm"
                        disabled={busy}
                        onClick={() => handleAddress(q.question_id)}
                      >
                        {busy ? 'Marking…' : 'Mark addressed'}
                      </button>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
};

export default RaisedQuestionsControl;
